import React, { Component, Fragment } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl-next';
import PropTypes from 'prop-types';
import { Mutation } from 'react-apollo';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { withAnalyticsEvents, type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';

import { FlagsStateContainer } from '@confluence/flags';

import type { WithIsSiteAdminProps } from '../higher-order';
import { withIsSiteAdmin } from '../higher-order';
import { LoadableChangeEditionNotificationDialog } from '../NotificationDialog';
import { LearnMoreButton } from '../Upgrade/LearnMoreButton';

import { ChangeEditionSectionMessage } from './ChangeEditionSectionMessage';
import { OpenSpacePermissionsMutation } from './OpenSpacePermissionsMutation.graphql';

const i18n = defineMessages({
	cancelButton: {
		id: 'change-edition.general.button.cancel',
		defaultMessage: 'Cancel',
		description: 'Cancel button',
	},
	grandSpacePermissionsConfirmationSuccessTitle: {
		id: 'change-edition.notification.dialog.grand-permissions.confirmation.success.title',
		defaultMessage: 'Space permissions granted',
		description: 'Success confirmation title for space',
	},
	grandSpacePermissionsConfirmationSuccessDesc: {
		id: 'change-edition.notification.dialog.grand-permissions.confirmation.success.desc',
		defaultMessage:
			'Anyone who’s logged in can now view {spaceName} space, add pages to it, and more.',
		description: 'Success confirmation description for space',
	},
	grandSpacePermissionsConfirmationFailedTitle: {
		id: 'change-edition.notification.dialog.grand-permissions.confirmation.failed.title',
		defaultMessage: 'Space permissions not granted',
		description: 'Failed confirmation title for space',
	},
	grandSpacePermissionsConfirmationFailedDesc: {
		id: 'change-edition.notification.dialog.grand-permissions.confirmation.failed.desc',
		defaultMessage:
			'We couldn’t grant space permissions to all users for the {spaceName} space. Try again.',
		description: 'Failed confirmation description for space',
	},
	siteAdminSpacePermissionsTitle: {
		id: 'change-edition.section.message.site-admin.space-permissions.title',
		defaultMessage: 'Upgrade for space permissions',
		description: 'Upgrade for space permissions title for site admins',
	},
	nonSiteAdminSpacePermissionsTitle: {
		id: 'change-edition.section.message.non-admin.space-permissions.title',
		defaultMessage: 'Space permissions are only available on paid plans',
		description: 'Space permissions title for non-site admins',
	},
	siteAdminSpaceBody: {
		id: 'change-edition.section.message.site-admin-space.body',
		defaultMessage:
			'You’re on the free plan, so you can’t set the default permissions for new spaces. Anyone who’s logged in can view new spaces, add pages to them, and more. To set default space permissions, upgrade your plan.',
		description: 'Warning message explaining why the form is not editable for site admins',
	},
	nonSiteAdminSpaceBody: {
		id: 'change-edition.section.message.non-admin-space.body',
		defaultMessage:
			'You’re on the free plan, so you can’t set the default permissions for new spaces. Anyone who’s logged in can view new spaces, add pages to them, and more.',
		description: 'Warning message explaining why the form is not editable for non-site admins',
	},
	spacePermissionsGrandfatheredText: {
		id: 'change-edition.section.message.space-permissions.grandfathered.text',
		defaultMessage:
			'You’re on the free plan, so you can’t change the space permissions. To set permissions on spaces, upgrade your plan. If needed, you can grant all users permissions for this space. (Anonymous users won’t be granted permissions.)',
		description: 'Warning message explaining why the form is not editable',
	},
	spacePermissionsGrandfatheredNoclearText: {
		id: 'change-edition.section.message.space-permissions.grandfathered.noclear.text',
		defaultMessage:
			'You’re on the free plan, so you can’t change the space permissions. To set permissions on spaces, upgrade your plan.',
		description: 'Warning message explaining why the form is not editable',
	},
	nonAdminSpacePermissionsGrandfatheredCanClearBody: {
		id: 'change-edition.section.message.non-site-admin.space-permissions.grandfathered.can.clear.body',
		defaultMessage:
			'You’re on the free plan, so you can’t change the space permissions. If needed, you can grant all users permissions for this space. (Anonymous users won’t be granted permissions.)',
		description: 'Warning message explaining why the form is not editable for non site-admins',
	},
	nonAdminSpacePermissionsGrandfatheredCantClearBody: {
		id: 'change-edition.section.message.non-site-admin.space-permissions.grandfathered.cant.clear.body',
		defaultMessage:
			'You’re on the free plan, so anyone who’s logged in can view this space, add pages to it, and more.',
		description: 'Warning message explaining why the form is not editable for non site-admins',
	},
	grantAllButton: {
		id: 'change-edition.section.message.space-permissions.grandfathered.button.link',
		defaultMessage: 'Grant all users permissions for this space',
		description: 'Granting permissions button',
	},
	grandSpacePermissionsTitle: {
		id: 'change-edition.notification.dialog.grand-permissions.title',
		defaultMessage: 'Grant space permissions?',
		description: 'grand space permissions title',
	},
	grantPermissionsButton: {
		id: 'change-edition.notification.dialog.general.button.grant-permissions',
		defaultMessage: 'Grant permissions',
		description: 'Grant permissions button',
	},
	grandSpacePermissionsText: {
		id: 'change-edition.notification.dialog.grand-permissions.text',
		defaultMessage:
			'You’re about to grant all users permissions for this space. <b>This can’t be undone.</b> You won’t be able to reset the space permissions later.',
		description: 'Remove restrictions title',
	},
});

type ChangeEditionSectionMessageSpaceComponentState = {
	showConfirmationDialog: boolean;
	isMutationInProcess: boolean;
};

const DEFAULT_COMPONENT_STATE: ChangeEditionSectionMessageSpaceComponentState = {
	showConfirmationDialog: false,
	isMutationInProcess: false,
};

type ChangeEditionSectionMessageSpaceComponentProps = {
	grandfathered: boolean;
	canClear: boolean;
	spaceName?: string;
	spaceKey?: string;
} & WithIsSiteAdminProps &
	WithAnalyticsEventsProps &
	WrappedComponentProps;

export class ChangeEditionSectionMessageSpaceComponent extends Component<
	ChangeEditionSectionMessageSpaceComponentProps,
	ChangeEditionSectionMessageSpaceComponentState
> {
	static propTypes = {
		grandfathered: PropTypes.bool.isRequired,
		canClear: PropTypes.bool.isRequired,
		spaceName: PropTypes.string,
		spaceKey: PropTypes.string,
		// https://github.com/formatjs/formatjs/issues/2695
		intl: PropTypes.any.isRequired,
		createAnalyticsEvent: PropTypes.func.isRequired,
		isSiteAdmin: PropTypes.bool.isRequired,
	};

	state = {
		...DEFAULT_COMPONENT_STATE,
	};

	openConfirmationDialog = () => {
		return this.setState({ showConfirmationDialog: true });
	};

	closeConfirmationDialog = () => {
		return this.setState(DEFAULT_COMPONENT_STATE);
	};

	applyChanges = (mutationFunction) => () => {
		const { spaceKey, createAnalyticsEvent, grandfathered, isSiteAdmin } = this.props;

		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'freeRemovePageRestrictionsConfirmation',
					source: 'individualSpacePermission',
					attributes: {
						isGrandfathered: grandfathered,
						isSiteAdmin,
						spaceKey,
					},
				},
			}).fire();
		}

		this.setState({
			isMutationInProcess: true,
		});
		mutationFunction();
	};

	handleMutationComplete = (flags) => () => {
		const { intl, spaceName, spaceKey, createAnalyticsEvent } = this.props;

		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'granted',
					actionSubject: 'allUserPermissions',
					actionSubjectId: 'freeGrantAllUserPermissions',
					source: 'individualSpacePermission',
					attributes: {
						spaceKey,
					},
				},
			}).fire();
		}

		flags.showSuccessFlag({
			title: intl.formatMessage(i18n.grandSpacePermissionsConfirmationSuccessTitle),
			description: intl.formatMessage(i18n.grandSpacePermissionsConfirmationSuccessDesc, {
				spaceName,
			}),
		});
		this.closeConfirmationDialog();

		window.setTimeout(() => {
			// we need to refresh to update section message
			window.location.reload();
		}, 500);
	};

	handleMutationError = (flags) => () => {
		const { intl, spaceName } = this.props;

		flags.showErrorFlag({
			title: intl.formatMessage(i18n.grandSpacePermissionsConfirmationFailedTitle),
			description: intl.formatMessage(i18n.grandSpacePermissionsConfirmationFailedDesc, {
				spaceName,
			}),
		});
		this.closeConfirmationDialog();
	};

	getBodyMessage() {
		const { intl, grandfathered, canClear, isSiteAdmin } = this.props;

		if (isSiteAdmin) {
			if (grandfathered && canClear) {
				// site admin who can clear grandfathered pages
				return (
					<>
						{intl.formatMessage(i18n.spacePermissionsGrandfatheredText)}{' '}
						<LearnMoreButton
							appearance="inline-link"
							analyticsProps={{
								source: 'individualSpacePermission',
								attributes: {
									isGrandfathered: true,
								},
							}}
						/>
					</>
				);
			} else {
				// site admin without any grandfathered pages
				return intl.formatMessage(i18n.spacePermissionsGrandfatheredNoclearText);
			}
		} else {
			if (grandfathered && canClear) {
				return intl.formatMessage(i18n.nonAdminSpacePermissionsGrandfatheredCanClearBody);
			} else {
				// non-site admin without any grandfathered pages
				return intl.formatMessage(i18n.nonAdminSpacePermissionsGrandfatheredCantClearBody);
			}
		}
	}

	getGrantPermissionsButtonProps() {
		const { canClear, grandfathered } = this.props;

		return canClear && grandfathered
			? {
					messageDescriptor: i18n.grantAllButton,
					onClick: this.openConfirmationDialog,
					actionSubjectId: 'freeGrantAllUserPermissions',
				}
			: undefined;
	}

	getTitle() {
		const { intl, isSiteAdmin } = this.props;
		let title = i18n.siteAdminSpacePermissionsTitle;
		if (!isSiteAdmin) {
			title = i18n.nonSiteAdminSpacePermissionsTitle;
		}

		return intl.formatMessage(title);
	}

	render() {
		const { intl, spaceKey, grandfathered } = this.props;
		const { showConfirmationDialog, isMutationInProcess } = this.state;

		const grantPermissionsButtonProps = this.getGrantPermissionsButtonProps();

		return (
			<Fragment>
				<ChangeEditionSectionMessage
					title={this.getTitle()}
					body={this.getBodyMessage()}
					secondaryLink={grantPermissionsButtonProps}
					source="individualSpacePermission"
					grandfathered={grandfathered}
				/>
				<Subscribe to={[FlagsStateContainer]}>
					{(flags: FlagsStateContainer) => (
						<Mutation
							mutation={OpenSpacePermissionsMutation}
							variables={{ spaceKey }}
							onCompleted={this.handleMutationComplete(flags)}
							onError={this.handleMutationError(flags)}
						>
							{(removeRestrictions) => (
								<LoadableChangeEditionNotificationDialog
									isOpen={showConfirmationDialog}
									isProcessing={isMutationInProcess}
									heading={intl.formatMessage(i18n.grandSpacePermissionsTitle)}
									cancelAction={{
										text: intl.formatMessage(i18n.cancelButton),
										onClick: this.closeConfirmationDialog,
									}}
									applyAction={{
										text: intl.formatMessage(i18n.grantPermissionsButton),
										onClick: this.applyChanges(removeRestrictions),
									}}
									source="individualSpacePermission"
									grandfathered={grandfathered}
								>
									<FormattedMessage
										{...i18n.grandSpacePermissionsText}
										values={{
											b: (...chunks) => <b>{chunks}</b>,
										}}
									/>
								</LoadableChangeEditionNotificationDialog>
							)}
						</Mutation>
					)}
				</Subscribe>
			</Fragment>
		);
	}
}

export const ChangeEditionSectionMessageSpace = withAnalyticsEvents()(
	injectIntl(withIsSiteAdmin(ChangeEditionSectionMessageSpaceComponent)),
);
